// The last step of the reel flow: give it a soundtrack, hear it here, keep it.
//
// Music is the one thing a finished reel is still missing, so it belongs in the page next to
// the reel — not behind the editor. The host generates a bed, plays it against the reel right
// here, asks for another take if they want one, and only then does a render make a new version.
import { useEffect, useRef, useState } from 'react';
import { CREDIT_COST } from '@str/shared';
import type { GenerationDto, VersionDto } from '@str/api/types';
import { useCreditsSheet } from '@/components/CreditsSheetHost';
import { creditShortfall, errorMessage } from '@/lib/api';
import { useMe } from '@/lib/queries';
import { MUSIC_PRICE, MUSIC_PRICE_LINE } from '@/lib/reel-flow';
import { generateReelMusic, reelDraft, reelTracks, renderReel } from '@/lib/reel';
import { defaultReelSound, ORIGINAL_UNDER_MUSIC } from '@/lib/reel-sound';

type Track = { id: string; title: string; url: string; durationSeconds: number };

/** How a host describes the feel they want, in their words, over the moods the prompt knows. */
const MOODS = [
  { id: 'calm', label: 'Calm' },
  { id: 'cozy', label: 'Cozy' },
  { id: 'cinematic', label: 'Cinematic' },
  { id: 'adventurous', label: 'Upbeat' },
  { id: 'romantic', label: 'Romantic' },
  { id: 'family', label: 'Family' },
] as const;
type Mood = (typeof MOODS)[number]['id'];

export function ReelSoundStep({
  postId,
  propertyId,
  version,
  disabled,
  onAccepted,
  previewVideo,
}: {
  postId: string;
  propertyId: string;
  version: VersionDto;
  disabled: boolean;
  onAccepted: (job: GenerationDto) => void;
  /** The reel playing on the stage, so the track can be heard over it. */
  previewVideo: () => HTMLVideoElement | null;
}) {
  const [track, setTrack] = useState<Track | null>(null);
  const [mood, setMood] = useState<Mood>('calm');
  const [making, setMaking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const player = useRef<HTMLAudioElement>(null);
  const credits = useCreditsSheet();
  const balance = useMe().data?.credits?.balance ?? null;

  // The track already mixed into this version, so the step opens on what the host chose.
  const chosen = version.videoEdit?.audio;
  const chosenId = chosen?.kind === 'media' ? chosen.mediaId : null;
  // Composer refetches the post while a take is generating, which remounts this step — so the
  // chosen track is recovered from the library rather than held only in memory.
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    let live = true;
    void reelTracks(propertyId)
      .then((tracks) => {
        if (!live) return;
        const found =
          tracks.find((t) => t.id === chosenId) ??
          // Newest take made for this reel but not yet kept.
          tracks.find((t) => t.postId === postId && t.source === 'generated');
        if (found) setTrack(found);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [chosenId, propertyId, postId, making]);

  const busy = disabled || making || saving;
  const shown = dismissed ? null : track;
  /** Already on the version: nothing to render, so the step reads as done. */
  const applied = !!track && track.id === chosenId;

  function make() {
    // An empty wallet opens the sheet before anything is composed; buying retries the ask.
    if (balance !== null && balance < CREDIT_COST.musicTrack)
      credits.open(CREDIT_COST.musicTrack, compose, 'compose a song');
    else compose();
  }

  /** The paid call itself: the sheet's retry comes straight here, past the stale wallet check. */
  function compose() {
    setError(null);
    setDismissed(false);
    setMaking(true);
    void generateReelMusic(postId, mood)
      .then((made) => {
        setTrack({
          id: made.id,
          title: made.label || 'Soundtrack',
          url: `/api/media/${made.id}/file`,
          durationSeconds: made.durationSeconds ?? 0,
        });
      })
      .catch((e) => {
        const short = creditShortfall(e);
        if (short)
          credits.open(short.needed, compose, 'compose a song', undefined, {
            balance: short.balance,
            actions: short.actions,
          });
        else setError(errorMessage(e));
      })
      .finally(() => setMaking(false));
  }

  /** Play the bed over the reel, so it is judged against the pictures and not on its own. */
  function playWithReel() {
    const audio = player.current;
    const video = previewVideo();
    if (!audio) return;
    audio.currentTime = 0;
    if (video) {
      video.currentTime = 0;
      // Heard as it will render: the place's own sound under the bed.
      video.volume = ORIGINAL_UNDER_MUSIC;
      video.muted = false;
      void video.play().catch(() => {});
    }
    void audio.play().catch(() => {});
  }

  function keep() {
    if (!track) return;
    setError(null);
    setSaving(true);
    const video = previewVideo();
    const durationMs = Math.floor((video?.duration || track.durationSeconds || 0) * 1000);
    const base = reelDraft(version);
    void renderReel(postId, propertyId, version, {
      ...base,
      // Clamp to the clip: a fresh draft's ninety-second default would run past the video.
      trim: { startMs: 0, endMs: durationMs > 0 ? durationMs : base.trim.endMs },
      audio: { kind: 'media', mediaId: track.id },
      audioFile: undefined,
      // The reel carries the place's own sound; the bed goes over it, not instead of it.
      sound: defaultReelSound(true),
    })
      .then(onAccepted)
      .catch((e) => setError(errorMessage(e)))
      .finally(() => setSaving(false));
  }

  return (
    <section className="nx-sound-step">
      <h3>♪ Sound</h3>
      <p className="nx-note">
        {shown
          ? 'Want a different feel? Pick one and ask for another take.'
          : 'Add music under the sound of your place. Pick how it should feel — the track is made for your place and cleared for social.'}
      </p>
      <p className="nx-note">{MUSIC_PRICE_LINE}</p>
      <div className="nx-chips" role="group" aria-label="How should it feel?">
        {MOODS.map((m) => (
          <button
            key={m.id}
            type="button"
            className={`nx-chip${mood === m.id ? ' on' : ''}`}
            aria-pressed={mood === m.id}
            disabled={busy}
            onClick={() => setMood(m.id)}
          >
            {m.label}
          </button>
        ))}
      </div>
      {!shown ? (
        <div className="nx-chips">
          <button type="button" className="nx-chip ai" disabled={busy} onClick={make}>
            {making ? 'Making music…' : `✦ Generate music · ${MUSIC_PRICE}`}
          </button>
        </div>
      ) : (
        <>
          <audio
            ref={player}
            controls
            preload="metadata"
            src={shown.url}
            style={{ width: '100%' }}
            aria-label={`Preview ${shown.title}`}
          />
          <p className="nx-note">
            {shown.title}
            {applied ? ' · on this reel' : ' · not on the reel yet'} · fades out at the end.
          </p>
          <div className="nx-chips">
            <button type="button" className="nx-chip" disabled={busy} onClick={playWithReel}>
              ▶ Play with the reel
            </button>
            <button type="button" className="nx-chip ai" disabled={busy} onClick={make}>
              {making ? 'Making music…' : `✦ Another take · ${MUSIC_PRICE}`}
            </button>
            {!applied && (
              <button type="button" className="nx-chip ai" disabled={busy} onClick={keep}>
                {saving ? 'Adding it…' : 'Use this music'}
              </button>
            )}
            <button
              type="button"
              className="nx-chip"
              disabled={busy}
              onClick={() => {
                player.current?.pause();
                setDismissed(true);
                setTrack(null);
              }}
            >
              No music
            </button>
          </div>
        </>
      )}
      {credits.element}
      {error && (
        <p role="alert" className="nx-note" style={{ color: '#b3261e' }}>
          {error}
        </p>
      )}
    </section>
  );
}
